/**
 * OnboardingFocusRing — пульсирующий тёплый ореол точно вокруг
 * подсвечиваемой DOM-области.
 *
 * JB 2026-05-26: «онбординг везде хуй пойми куда указывает». Раньше
 * координаты в CSS были статичные и не совпадали с реальными элементами
 * на mobile. Теперь rect берётся из живого DOM (useAnchor regiester'ит
 * ref). При resize/scroll re-poll каждые 250ms — простое и надёжное.
 */
import { motion } from 'framer-motion';
import { useEffect, useState } from 'react';
import { getAnchorRect } from './anchors.js';
import type { TipAnchor } from './types.js';
import styles from './OnboardingFocusRing.module.css';

interface FocusRingProps {
  readonly anchor: TipAnchor;
}

/** Сколько пикселей запаса вокруг якоря — чтобы ring не прижимался вплотную.
 * JB 2026-05-26: 12→16, заметнее ореол вокруг тусклой disabled-кнопки. */
const PADDING = 16;

export function OnboardingFocusRing({ anchor }: FocusRingProps): JSX.Element | null {
  const [rect, setRect] = useState<DOMRect | null>(null);

  useEffect(() => {
    // top-banner и whisper якоря не имеют визуального ring — текст-плакат
    // и сам является фокусом. Пропускаем рендер чтобы не подсвечивать пусто.
    if (anchor === 'top-banner') {
      setRect(null);
      return;
    }

    const refresh = (): void => {
      const r = getAnchorRect(anchor);
      if (r === null) {
        setRect(null);
        return;
      }
      // Превращаем в DOMRect-подобный объект.
      setRect(
        new DOMRect(
          r.left - PADDING,
          r.top - PADDING,
          r.width + PADDING * 2,
          r.height + PADDING * 2,
        ),
      );
    };

    refresh();
    // Poll каждые 250ms чтобы ловить resize/scroll/layout shifts.
    const intervalId = window.setInterval(refresh, 250);
    return () => {
      window.clearInterval(intervalId);
    };
  }, [anchor]);

  if (rect === null) return null;

  return (
    <motion.div
      className={styles.ring}
      aria-hidden
      initial={{ opacity: 0, scale: 0.85 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.9 }}
      transition={{ duration: 0.4, ease: [0.25, 0.8, 0.3, 1] }}
      style={{
        position: 'fixed',
        top: `${rect.top.toString()}px`,
        left: `${rect.left.toString()}px`,
        width: `${rect.width.toString()}px`,
        height: `${rect.height.toString()}px`,
      }}
    />
  );
}
